import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, AlertCircle, Loader2, Printer, Download, X } from "lucide-react";
import { Button } from "../components/ui/button";

import { API_URL } from "../config";

const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return `rgba(245,158,11,${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const PaymentReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("processing");
  const [ticketNumber, setTicketNumber] = useState(null);
  const [show, setShow] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const registrationId = searchParams.get("registration_id") || localStorage.getItem("pending_registration_id");
  const stripeSessionId = searchParams.get("session_id");
  const isExternal = searchParams.get("external") === "true";
  const retryToken = searchParams.get("retry_token");

  useEffect(() => {
    axios.get(`${API_URL}/api/show`).then(res => setShow(res.data)).catch(() => {});
  }, []);

  // Fetch reservation status (countdown) — only when in cancelled / awaiting state
  useEffect(() => {
    if (!registrationId || status === "success") return;
    let cancelled = false;
    const fetchRes = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/reservation/${registrationId}`);
        if (cancelled) return;
        if (r.data.status === "expired") setSecondsLeft(0);
        else if (r.data.status === "paid") setSecondsLeft(null);
        else setSecondsLeft(r.data.seconds_remaining);
      } catch (e) { /* ignore */ }
    };
    fetchRes();
    const t = setInterval(fetchRes, 30000); // refresh every 30s
    return () => { cancelled = true; clearInterval(t); };
  }, [registrationId, status]);

  // Local 1s tick to render smooth countdown
  useEffect(() => {
    if (secondsLeft === null || secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [secondsLeft !== null]);

  const formatCountdown = (s) => {
    if (s === null || s === undefined) return null;
    if (s <= 0) return "0:00";
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  const handleCancelReservation = async () => {
    if (!registrationId) return navigate("/");
    setCancelling(true);
    try {
      await axios.post(`${API_URL}/api/reservation/${registrationId}/cancel`);
      localStorage.removeItem("pending_registration_id");
      toast.success("Reserva cancelada. O lugar foi libertado.");
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível cancelar.");
      setCancelling(false);
    }
  };

  // Intercept "Voltar ao Início" — only ask if reservation is still active
  const handleBackHome = () => {
    if (secondsLeft !== null && secondsLeft > 0) {
      setShowLeaveModal(true);
    } else {
      navigate("/");
    }
  };

  const pollStripeStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("error");
      toast.error("Tempo esgotado. Verifique o seu email para confirmação.");
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/stripe/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        const regId = data.metadata?.registration_id || registrationId;
        if (regId) {
          try {
            const regResponse = await axios.get(`${API_URL}/api/ticket-by-registration/${regId}`);
            setTicketNumber(regResponse.data.ticket_number);
          } catch {
            setTicketNumber(data.metadata?.ticket_number || "");
          }
        }
        setStatus("success");
        localStorage.removeItem("pending_registration_id");
        toast.success("Pagamento confirmado com sucesso!");
        return;
      } else if (data.status === "expired") {
        setStatus("error");
        toast.error("Sessão de pagamento expirada.");
        return;
      }

      setTimeout(() => pollStripeStatus(sessionId, attempts + 1), pollInterval);
    } catch {
      setTimeout(() => pollStripeStatus(sessionId, attempts + 1), pollInterval);
    }
  }, [registrationId]);

  const confirmPayment = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/confirm-payment`, {
        registration_id: registrationId,
      });
      setTicketNumber(response.data.ticket_number);
      setStatus("success");
      localStorage.removeItem("pending_registration_id");
      toast.success("Pagamento confirmado com sucesso!");
    } catch (error) {
      setStatus("error");
      toast.error(error.response?.data?.detail || "Erro ao confirmar pagamento");
    }
  };

  const isCancelled = searchParams.get("cancelled") === "true";
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (retryToken && registrationId) {
      // Coming from email retry link - validate and redirect to Stripe
      handleRetryFromEmail();
    } else if (isCancelled && registrationId) {
      setStatus("cancelled");
      // Send first payment reminder email
      axios.post(`${API_URL}/api/payment/cancelled`, { registration_id: registrationId }).catch(() => {});
    } else if (stripeSessionId) {
      pollStripeStatus(stripeSessionId);
    } else if (isExternal && registrationId) {
      setStatus("external_pending");
      localStorage.removeItem("pending_registration_id");
    } else if (registrationId) {
      confirmPayment();
    } else {
      setStatus("error");
    }
  }, [stripeSessionId, registrationId, isExternal, isCancelled, retryToken, pollStripeStatus]);

  const handleRetryFromEmail = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payment/retry?retry_token=${retryToken}&registration_id=${registrationId}`);
      if (response.data.already_paid) {
        setStatus("success");
        toast.success("Este bilhete já foi pago!");
        return;
      }
      // Redirect to Stripe
      handleRetryPayment();
    } catch (error) {
      setStatus("error");
      toast.error(error.response?.data?.detail || "Link expirado ou inválido");
    }
  };

  const handleRetryPayment = async () => {
    setRetrying(true);
    try {
      const response = await axios.post(`${API_URL}/api/stripe/create-checkout`, {
        registration_id: registrationId,
        origin_url: window.location.origin
      });
      if (response.data.free) {
        window.location.href = `/payment-return?registration_id=${registrationId}`;
        return;
      }
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao iniciar pagamento");
      setRetrying(false);
    }
  };

  const ac = show?.accent_color || "#f59e0b";

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6" data-testid="payment-return-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="glass rounded-2xl p-8 max-w-md w-full text-center"
        style={{ borderColor: hexToRgba(ac, 0.2) }}
      >
        {status === "processing" && (
          <div data-testid="payment-processing">
            <Loader2 className="w-16 h-16 animate-spin mx-auto mb-6" style={{ color: ac }} />
            <h2 className="text-2xl font-bold text-white mb-4">A confirmar pagamento...</h2>
            <p className="text-zinc-400">Por favor, aguarde enquanto processamos o seu pagamento.</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="payment-success">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: hexToRgba(ac, 0.2) }}>
              <Check className="w-8 h-8" style={{ color: ac }} />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Pagamento Confirmado!</h2>
            <p className="text-zinc-400 mb-6">
              O seu bilhete foi enviado para o seu email. Também pode visualizá-lo aqui.
            </p>
            {ticketNumber && (
              <div className="rounded-xl p-4 mb-6" style={{ backgroundColor: hexToRgba(ac, 0.08), border: `1px solid ${hexToRgba(ac, 0.2)}` }}>
                <p className="text-zinc-400 text-sm">Número do Bilhete</p>
                <p className="text-xl font-bold" style={{ color: ac }} data-testid="ticket-number">{ticketNumber}</p>
              </div>
            )}
            <div className="flex flex-col gap-3">
              {ticketNumber && (
                <Button
                  onClick={() => navigate(`/ticket/${ticketNumber}`)}
                  className="py-6 text-black font-semibold"
                  style={{ backgroundColor: ac }}
                  data-testid="view-ticket-btn"
                >
                  Ver Bilhete
                </Button>
              )}
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="btn-secondary"
              >
                Voltar ao Início
              </Button>
            </div>
          </div>
        )}

        {status === "external_pending" && (
          <div data-testid="payment-external-pending">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: hexToRgba(ac, 0.2) }}>
              <Check className="w-8 h-8" style={{ color: ac }} />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Registo Efetuado!</h2>
            <p className="text-zinc-400 mb-6">
              O bilhete só será enviado por email após a confirmação do pagamento pelo sistema.
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate("/")}
                className="py-6 text-black font-semibold"
                style={{ backgroundColor: ac }}
              >
                Voltar ao Início
              </Button>
            </div>
          </div>
        )}

        {status === "cancelled" && (
          <div data-testid="payment-cancelled">
            <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
              <X className="w-8 h-8 text-yellow-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Pagamento Não Concluído</h2>
            <p className="text-zinc-400 mb-4">
              O pagamento foi cancelado ou não foi concluído.
            </p>

            {/* Countdown card */}
            {secondsLeft !== null && secondsLeft > 0 && (
              <div
                className="rounded-xl p-4 mb-5 border"
                style={{ borderColor: `${ac}55`, background: `${ac}11` }}
                data-testid="reservation-countdown"
              >
                <div className="text-zinc-400 text-xs uppercase tracking-wider">Reserva expira em</div>
                <div className="text-3xl font-black mt-1 font-mono" style={{ color: ac }}>
                  {formatCountdown(secondsLeft)}
                </div>
                <p className="text-zinc-500 text-xs mt-1">
                  Após este tempo a reserva expira e o lugar fica disponível para outros.
                </p>
              </div>
            )}
            {secondsLeft === 0 && (
              <div className="rounded-xl p-4 mb-5 border border-red-500/40 bg-red-500/10" data-testid="reservation-expired">
                <p className="text-red-300 text-sm">A reserva expirou. Por favor faça novo registo na página inicial.</p>
              </div>
            )}

            <div className="flex flex-col gap-3">
              {secondsLeft !== 0 && (
                <Button
                  onClick={handleRetryPayment}
                  disabled={retrying}
                  className="py-6 text-black font-semibold"
                  style={{ backgroundColor: ac }}
                  data-testid="btn-retry-payment"
                >
                  {retrying ? "A redirecionar..." : "Tentar Novamente"}
                </Button>
              )}
              <Button
                onClick={handleBackHome}
                variant="outline"
                className="py-6 border-white/10 text-zinc-400 hover:text-white"
                data-testid="btn-back-home"
              >
                Voltar ao Início
              </Button>
              {secondsLeft > 0 && (
                <button
                  onClick={() => setShowLeaveModal(true)}
                  disabled={cancelling}
                  className="text-zinc-500 hover:text-red-400 text-xs underline transition-colors mt-1 disabled:opacity-50"
                  data-testid="btn-cancel-reservation"
                >
                  Cancelar reserva e libertar o lugar
                </button>
              )}
            </div>
          </div>
        )}

        {status === "error" && (
          <div data-testid="payment-error">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Erro no Pagamento</h2>
            <p className="text-zinc-400 mb-6">
              Não foi possível confirmar o seu pagamento. Por favor, tente novamente ou entre em contacto connosco.
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => {
                  setStatus("processing");
                  if (stripeSessionId) pollStripeStatus(stripeSessionId);
                  else confirmPayment();
                }}
                className="bg-red-600 hover:bg-red-700 text-white py-6"
                data-testid="retry-payment-btn"
              >
                Tentar Novamente
              </Button>
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="btn-secondary"
              >
                Voltar ao Início
              </Button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Leave-confirmation modal: pops when user tries to go back home with active reservation */}
      {showLeaveModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.85)" }}
          data-testid="leave-confirm-modal"
        >
          <div
            className="bg-zinc-900 rounded-2xl max-w-md w-full p-6 md:p-7 border"
            style={{ borderColor: `${ac}55`, boxShadow: `0 0 0 1px ${ac}33, 0 30px 80px rgba(0,0,0,0.6)` }}
          >
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: `${ac}1a`, border: `1px solid ${ac}55` }}
            >
              <span className="text-3xl">⚠️</span>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-white text-center mb-3">
              Cancelar a reserva?
            </h2>
            <p className="text-zinc-400 text-sm md:text-base text-center mb-2 leading-relaxed">
              Ao voltar ao início agora <strong>cancela a sua reserva</strong> e o lugar fica disponível para outros compradores.
            </p>
            {secondsLeft !== null && secondsLeft > 0 && (
              <p className="text-zinc-500 text-xs text-center mb-5">
                Ainda tem <span className="font-mono font-bold" style={{ color: ac }}>{formatCountdown(secondsLeft)}</span> para concluir o pagamento.
              </p>
            )}
            <div className="flex flex-col gap-3 mt-6">
              <button
                onClick={() => setShowLeaveModal(false)}
                className="w-full px-4 py-3 rounded-xl text-black font-semibold text-sm transition-transform hover:scale-[1.02]"
                style={{ background: ac }}
                data-testid="leave-stay-btn"
              >
                Não — quero fazer o pagamento agora
              </button>
              <button
                onClick={async () => {
                  setShowLeaveModal(false);
                  await handleCancelReservation();
                }}
                disabled={cancelling}
                className="w-full px-4 py-3 rounded-xl border border-red-500/30 text-red-300 hover:bg-red-500/10 text-sm font-medium transition-colors disabled:opacity-50"
                data-testid="leave-confirm-btn"
              >
                {cancelling ? "A cancelar…" : "Sim, cancelar reserva e voltar ao início"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentReturn;
